import { useState } from 'react'
import { Table, Text, Badge, Paper, Title, Box } from '@mantine/core'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { showNotification } from '../util/notifications'
import { useQuery, useMutation } from '@apollo/client/react'
import PublicRooms from '../graphql/queries/PublicRooms'
import JoinRoom from '../graphql/queries/JoinRoom'
import CurrentUser from '../graphql/queries/CurrentUser'
import useCurrentUser from '../hooks/useCurrentUser'

export default function PublicRoomsSection({ onNavigate }) {
    const { data, loading } = useQuery(PublicRooms, {
        fetchPolicy: 'cache-and-network'
    })
    const rooms = data?.publicRooms?.rooms ?? []
    const dispatch = useAppDispatch()
    const currentUser = useCurrentUser()
    const [joiningRoomId, setJoiningRoomId] = useState(null)

    const [joinRoomMutation] = useMutation(JoinRoom, {
        refetchQueries: [{ query: CurrentUser }]
    })

    async function navigateToRoom(room) {
        console.log()
        // Check if user is already a member
        const isMember = currentUser?.rooms?.some(r => r.id === room.id)

        if (!isMember) {
            // Join the room first
            setJoiningRoomId(room.id)
            try {
                await joinRoomMutation({ variables: { roomId: room.id } })
            } catch (error) {
                console.error('Failed to join room:', error)
                setJoiningRoomId(null)
                showNotification({
                    text: 'Failed to join room. Please try again.',
                    type: 'error'
                })
                return
            }
            setJoiningRoomId(null)
        }

        // Navigate to the room
        dispatch(Actions.setCurrentRoom(room.id))
        dispatch(Actions.navigateTo('listen'))
        onNavigate?.()
    }

    function getActiveCount(room) {
        const RECENT_WINDOW_MS = 60 * 60 * 1000
        const nowTs = Date.now()
        return room.members?.filter(m => (
            typeof m.lastActivity === 'number' && (nowTs - m.lastActivity) <= RECENT_WINDOW_MS
        )).length ?? 0
    }

    if (loading && rooms.length === 0) {
        return (
            <Box>
                <Title order={5} c="gray.3" mb="xs" style={{ textAlign: 'left' }}>Public Rooms</Title>
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                        Loading...
                    </Text>
                </Paper>
            </Box>
        )
    }

    if (!rooms || rooms.length === 0) {
        return (
            <Box>
                <Title order={5} c="gray.3" mb="xs" style={{ textAlign: 'left' }}>Public Rooms</Title>
                <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                        No public rooms available.
                    </Text>
                </Paper>
            </Box>
        )
    }

    return (
        <Box>
            <Title order={5} c="gray.3" mb="xs" style={{ textAlign: 'left' }}>Public Rooms</Title>
            <Paper p="md" radius="md" withBorder style={{ background: 'rgba(255,255,255,0.05)' }}>
                <Table highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{ textAlign: 'left' }}>Name</Table.Th>
                            <Table.Th style={{ textAlign: 'left' }}>Topic</Table.Th>
                            <Table.Th style={{ textAlign: 'center' }}>Active</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rooms.map((room) => {
                            const isJoining = joiningRoomId === room.id
                            return (
                                <Table.Tr
                                    key={room.id}
                                    style={{
                                        cursor: isJoining ? 'wait' : 'pointer',
                                        opacity: isJoining ? 0.6 : 1
                                    }}
                                    onClick={() => !isJoining && navigateToRoom(room)}
                                >
                                    <Table.Td style={{ textAlign: 'left' }}>
                                        <Text c="indigo.4" fw={500} style={{ cursor: isJoining ? 'wait' : 'pointer' }}>
                                            {room.name}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'left' }}>
                                        <Text size="sm" c="dimmed">{room.topic || '—'}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Badge size="sm" variant="light" color="green">
                                            {getActiveCount(room)}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            )
                        })}
                    </Table.Tbody>
                </Table>
            </Paper>
        </Box>
    )
}

